import React, { useState, Suspense } from "react";
import { useTranslation } from "utilities/i18n";
import ViewSelector from "views/ViewSelector/ViewSelector";
import {
  DeviceFilter,
  areFiltersSelected,
  useDeviceFilterValue,
  EdgeLink,
  ErrorPage,
  Loader
} from "elements";
import { EdgeLinkProps } from "elements/EdgeLink";
import { hasValue } from "utilities/filters/filters";
import { verifyAndFormatAggregates } from "utilities/filters/deviceFilters";
import { DevicesLocationQuery } from "generated/graphql";
import { FilterPanel } from "collections/FilterPanel";
import { useDevices } from "./useDevices";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import { clearGeofilter } from "store/devicesMap";
import "./MapView.scss";
import AppErrorBoundary from "routes/AppErrorBoundary";
import { useDeviceCount } from "./useDeviceCount";
import { NoDevicesIndicator } from "./NoDevicesIndicator";
import { Layout } from "views/Main/Layouts";

const Map = React.lazy(() => import("collections/Map"));

const UnknownCount: React.FC<{ count: number }> = ({ count }) => {
  const { t } = useTranslation();
  return count === 0 ? null : (
    <div className="appendix">
      <div className="count-label">{count}</div>
      <p>{t("NO_LOCATION_DATA")}</p>
    </div>
  );
};

const LazyFilters: React.FC<{ collapsible?: boolean }> = ({ collapsible }) => {
  const { t } = useTranslation();
  const [filterValues] = useDeviceFilterValue();
  const dispatch = useDispatch();
  const geofilter = useSelector((state: State) => state.devicesMap.geofilter);

  let aggregates: DevicesLocationQuery["deviceAggregations"] = [];
  let unknownCount = 0;
  let totalCount = 0;

  const { fetching, data } = useDevices(geofilter);

  const doClearGeofilter = () => {
    dispatch(clearGeofilter());
  };

  if (
    data &&
    data.deviceAggregations &&
    data.unknownLocationCount !== undefined
  ) {
    unknownCount = data.unknownLocationCount;
    totalCount = data.deviceCount;
    aggregates = data.deviceAggregations;
  }

  const verifyAndFormat = verifyAndFormatAggregates(aggregates);

  const models = verifyAndFormat("hardwareModel");
  const versions = verifyAndFormat("softwareVersion");
  const sites = verifyAndFormat("site");
  const connection = verifyAndFormat("connected").map(status => ({
    value: status.value === "1" ? "true" : "false",
    count: status.count
  }));
  const [filterState, setFilter, resetFilters] = useDeviceFilterValue();

  let filterArray = [
    <DeviceFilter
      title={t("DEVICE_MODEL")}
      field="model"
      options={models.map(({ value, count }) => ({
        text: value,
        value: value,
        count: count
      }))}
      filterValues={filterState}
      onChange={value => {
        if (value === "" && hasValue(filterState, "version")) {
          return setFilter({
            ...filterState,
            model: undefined,
            version: undefined
          });
        }
        return setFilter({
          ...filterState,
          model: value !== "" ? value : undefined
        });
      }}
      loading={fetching}
      search
      clearable
    />,
    <DeviceFilter
      title={t("SITE")}
      field="site"
      options={sites.map(({ value, count }) => ({
        text: value,
        value: value,
        count: count
      }))}
      enableTooltip
      filterValues={filterState}
      onChange={value =>
        setFilter({
          ...filterState,
          site: value !== "" ? value : undefined
        })
      }
      loading={fetching}
      search
      clearable
    />,
    <DeviceFilter
      title={t("STATUS")}
      field="connected"
      options={connection.map(({ value, count }) => ({
        text: value === "true" ? t("ONLINE") : t("OFFLINE"),
        value: value,
        count: count
      }))}
      filterValues={filterState}
      onChange={value =>
        setFilter({
          ...filterState,
          connected: value !== "" ? value : undefined
        })
      }
      loading={fetching}
      clearable
    />
  ];

  // Design only wants the version filter to exist if the user
  // has already selected a model, here will will check for a
  // model filter value and then splice the version filter
  // after the model filter (Repeated in TableView.tsx)
  if (hasValue(filterValues, "model")) {
    filterArray.splice(
      1,
      0,
      <DeviceFilter
        title={t("VERSION")}
        field="version"
        options={versions.map(({ value, count }) => ({
          text: value,
          value: value,
          count: count
        }))}
        filterValues={filterState}
        onChange={value =>
          setFilter({
            ...filterState,
            version: value !== "" ? value : undefined
          })
        }
        loading={fetching}
        search
        clearable
      />
    );
  }

  return (
    <FilterPanel
      collapsible={collapsible}
      geofiltered
      filters={filterArray}
      areFiltersSelected={areFiltersSelected}
      isGeofilterActive={() => geofilter}
      filterValues={filterValues}
      resetFilters={resetFilters}
      clearGeofilter={doClearGeofilter}
      metric={{
        type: t("DEVICES"),
        count: totalCount
      }}
      appendix={<UnknownCount count={unknownCount} />}
    />
  );
};

const MapView: React.FC = () => {
  const { t } = useTranslation();
  const [linkVisible, setLinkVisible] = useState(true);

  // total device count without any filters applied
  // need this count to decide whether to show empty state for devices
  const { count, fetching } = useDeviceCount();

  const doClose = () => {
    setLinkVisible(false);
  };

  const linkProps: EdgeLinkProps = {
    text: `${t("VIEW_DEVICES_IN_LIST")}`,
    linkTo: "/manage/inventory/table",
    keepParams: ["DeviceFilterState"],
    onClose: doClose
  };

  const { Sidebar, Content } = Layout;
  return (
    <Loader loading={fetching}>
      <div data-testid="map-view">
        {count === 0 ? (
          <Layout viewSelector={<ViewSelector />}>
            <Sidebar>
              <LazyFilters />
            </Sidebar>
            <Content>
              <NoDevicesIndicator />
            </Content>
          </Layout>
        ) : (
          <div
            className="main-content map-view-content scroll-content"
            data-testid="map-container"
          >
            <ViewSelector className="fixed" />
            <AppErrorBoundary
              errorComponent={<ErrorPage k="ERROR_LOADING_MAP" />}
            >
              <Suspense fallback={<p>Loading map</p>}>
                <Map filters={<LazyFilters collapsible />} />
              </Suspense>
            </AppErrorBoundary>
            {linkVisible && <EdgeLink {...linkProps} />}
          </div>
        )}
      </div>
    </Loader>
  );
};

export default MapView;
